import dynamic from "next/dynamic";

import { useCallback, useEffect, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { filter } from "lodash";
import { twMerge } from "tailwind-merge";

import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";

import FontAwesomeIcon from "components/FontAwesomeIcon";
import useSignInSignUp from "components/SignInSignUpModal/useSignInSignUp";
import useActivityTracker from "hooks/useActivityTracker";
import { useApi } from "hooks/useApi";
import useAuthentication from "hooks/useAuthentication";
import { useGlobalState } from "hooks/useGlobalState";
import { baseFetcher, fetchApi } from "utils/baseFetcher";

import { analystsUrl } from "./FollowTeamModal";

const FollowTeamModal = dynamic(() => import("./FollowTeamModal"));

type FollowButtonProps = {
  profile: any;
  onFollow?: (_profile: any) => void;

  style?: "full" | "small" | "card" | "text";
  className?: string;
  hoverClassName?: string;
  activeClassName?: string;
  showIcon?: boolean;
  alwaysShow?: boolean;
  hideTextOnActive?: boolean;
  hideText?: boolean;
  hideOnActive?: boolean;
  company?: any;
};

const FollowButton = ({
  profile,
  onFollow,
  style,
  className,
  hoverClassName,
  activeClassName,
  showIcon,
  alwaysShow,
  hideTextOnActive,
  hideText,
  hideOnActive,
  company,
}: FollowButtonProps) => {
  const { authenticated, account } = useAuthentication();
  const { trackEvent } = useActivityTracker();
  const { toggleModal } = useSignInSignUp();

  const [isFollowed, setIsFollowed, _, dispatch] = useGlobalState(
    `profile-follow${
      profile?.isCompany ? `-company-${profile?.id}` : `-${profile?.id}`
    }`,
    profile?.isFollowed
  );
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [hideAfterDelay, setHideAfterDelay] = useState(true);
  const { data: accounts } = useApi(
    company?.slug ? analystsUrl(company?.slug) : null,
    baseFetcher,
    {
      revalidateFirstPage: false,
      revalidateOnFocus: false,
    }
  );

  const follow = useCallback(
    (e: React.MouseEvent) => {
      if (e) {
        e.preventDefault();
      }

      if (!authenticated) {
        toggleModal("sign-in");
        return;
      }

      if (profile.isCompany) {
        setShowTeamModal(true);
        return;
      }

      const action = isFollowed ? "unfollow" : "follow";
      setIsFollowed(!isFollowed);

      setHideAfterDelay(false);
      setTimeout(() => {
        setHideAfterDelay(true);
      }, 1500);

      fetchApi(`/api/v2/accounts/${profile.slug}/${action}`, {
        method: "PATCH",
      })
        .then((response) => {
          // setIsFollowed(response.isFollowed);
          trackEvent("follow", { following: !isFollowed });
          if (onFollow)
            onFollow({ ...profile, isFollowed: response.isFollowed });
          if (company) {
            const followedAnalysts = filter(accounts?.data, (item) => {
              return item.isFollowed;
            });
            if (followedAnalysts.length + 1 >= company.analystsCount) {
              dispatch({
                type: "replace",
                key: `profile-follow-company-${company.id}`,
                data: true,
              });
            } else {
              dispatch({
                type: "replace",
                key: `profile-follow-company-${company.id}`,
                data: false,
              });
            }
          }
        })
        .catch(() => {
          // setIsFollowed(!isFollowed);
        });
    },
    [
      accounts?.data,
      authenticated,
      company,
      dispatch,
      isFollowed,
      onFollow,
      profile,
      setIsFollowed,
      toggleModal,
      trackEvent,
    ]
  );

  const onFollowAll = useCallback(
    (isAllFollowed) => {
      setIsFollowed(isAllFollowed);
    },
    [setIsFollowed]
  );

  useEffect(() => {
    setIsFollowed(profile?.isFollowed);
  }, [profile?.isFollowed, setIsFollowed]);

  useEffect(() => {
    if (isFollowed && profile?.isCompany) {
      setHideAfterDelay(false);
      setShowTeamModal(false);
      setHideAfterDelay(true);
    }
  }, [isFollowed, profile?.isCompany]);

  if (
    (!authenticated || account?.canFollow) &&
    (profile?.canBeFollowed || alwaysShow)
  ) {
    if (hideOnActive && hideAfterDelay && isFollowed) return <></>;

    if (style === "text") {
      return (
        <>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>{isFollowed ? "Following" : "Follow"}</Tooltip>}
          >
            <button
              className={twMerge(
                isFollowed
                  ? " text-bullish-green hover:text-bullish-green"
                  : " text-smart-teal hover:text-dark-teal ",
                "flex items-center justify-center",
                "border-none bg-transparent !leading-none",
                "p-0",
                className
              )}
              aria-label="follow"
              onClick={follow}
            >
              <FontAwesomeIcon
                icon={isFollowed ? faCheck : faPlus}
                className={twMerge(
                  "ml-[5px] mr-[2px] text-[10px]",
                  className,
                  isFollowed && activeClassName
                )}
              />
              {!hideText && !isFollowed && "Follow"}
            </button>
          </OverlayTrigger>
          {showTeamModal && (
            <FollowTeamModal
              onFollowAll={onFollowAll}
              profile={profile}
              onClose={() => setShowTeamModal(false)}
            />
          )}
        </>
      );
    }

    if (style === "card") {
      return (
        <>
          <div>
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip className="hidden md:block">
                  {isFollowed ? "Following" : "Follow"}
                </Tooltip>
              }
            >
              <button
                className={twMerge(
                  isFollowed
                    ? "from-smart-teal to-karma-green md:hover:bg-[#1d8988] bg-gradient-to-r text-white"
                    : "bg-white md:hover:bg-smart-teal text-smart-teal md:hover:text-white ",
                  !profile?.canBeFollowed && "cursor-normal opacity-50",
                  "border border-solid border-smart-teal ",
                  "rounded-full text-[13px] leading-[1] px-[10px] py-[6px]",
                  "flex",
                  className
                )}
                aria-label="follow"
                onClick={follow}
                disabled={!profile?.canBeFollowed}
              >
                <FontAwesomeIcon
                  icon={isFollowed ? faCheck : faPlus}
                  className="mr-[0.5em]"
                />
                {isFollowed ? "Following" : "Follow"}
              </button>
            </OverlayTrigger>
          </div>
          {showTeamModal && (
            <FollowTeamModal
              onFollowAll={onFollowAll}
              profile={profile}
              onClose={() => setShowTeamModal(false)}
            />
          )}
        </>
      );
    }

    if (style === "small") {
      return (
        <>
          <div>
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>{isFollowed ? "Following" : "Follow"}</Tooltip>}
            >
              <button
                className={twMerge(
                  isFollowed
                    ? "bg-white border-bullish-green md:hover:bg-bullish-green text-bullish-green hover:text-white"
                    : "bg-white border-smart-teal  md:hover:bg-smart-teal text-smart-teal hover:text-white ",
                  "border border-solid",
                  "rounded-full text-[12px] px-[1em] py-[0.75em]",
                  className
                )}
                aria-label="follow"
                onClick={follow}
              >
                <FontAwesomeIcon icon={isFollowed ? faCheck : faPlus} />
              </button>
            </OverlayTrigger>
          </div>
          {showTeamModal && (
            <FollowTeamModal
              onFollowAll={onFollowAll}
              profile={profile}
              onClose={() => setShowTeamModal(false)}
            />
          )}
        </>
      );
    }

    return (
      <>
        <OverlayTrigger
          placement="top"
          overlay={
            isFollowed ? (
              <Tooltip>You are following {profile?.name}</Tooltip>
            ) : (
              <></>
            )
          }
        >
          <button
            disabled={authenticated && !account?.canFollow}
            className={twMerge(
              isFollowed ? "bg-jade " : "bg-smart-teal ",
              account?.canFollow &&
                (isFollowed
                  ? "md:hover:bg-[#419855]"
                  : "md:hover:bg-[#1d8988]"),
              "border-none rounded-full",
              "text-white font-medium",
              "leading-[1] md:leading-normal",
              "text-[13px] md:text-[15px]",
              "px-[1.25em] py-[0.75em] md:py-[0.75em] md:px-[1.1em]",
              hideTextOnActive && isFollowed && "md:px-[0.8em]",
              className,
              account?.canFollow && hoverClassName,
              isFollowed && activeClassName
            )}
            aria-label="follow"
            onClick={follow}
          >
            {showIcon && (
              <FontAwesomeIcon
                icon={isFollowed ? faCheck : faPlus}
                className={twMerge(
                  (!hideTextOnActive || !isFollowed) && "mr-[5px]"
                )}
              />
            )}
            {(!hideTextOnActive || !isFollowed) &&
              (isFollowed ? "Following" : "Follow")}
          </button>
        </OverlayTrigger>
        {showTeamModal && (
          <FollowTeamModal
            profile={profile}
            onFollowAll={onFollowAll}
            onClose={() => setShowTeamModal(false)}
          />
        )}
      </>
    );
  }

  return <></>;
};

export default FollowButton;
